// Screen 4: PDF Reader + Notes + Highlights — deep-work mode.
// Layout: sidebar | reader-topbar | (outline | pdf viewer w/ highlight overlay | AI/notes rail)

import { streamChat } from '../src/lib/gemini.js';
import { useAuth } from '../src/lib/auth.jsx';
import { usePapers } from '../src/lib/use-papers.js';

const READER_PROMPTS = {
  simple:  { kind: 'simple',  labelKo: '간단 요약', labelEn: 'Simple summary',
             template: (text) => `다음 학술 문단을 비전공자도 이해하도록 3문장 이내로 한국어로 요약해주세요. 수식 기호는 원문 그대로 두세요.\n\n"""${text}"""` },
  formula: { kind: 'formula', labelKo: '수식 설명', labelEn: 'Explain math',
             template: (text) => `다음 문단의 수식을 한 줄씩 한국어로 설명하고, 각 기호가 무엇을 뜻하는지 표로 정리해주세요.\n\n"""${text}"""` },
  impl:    { kind: 'impl',    labelKo: '구현 분석', labelEn: 'Implementation',
             template: (text) => `다음 문단을 구현 관점에서 한국어로 분석해주세요: 어떤 자료구조/연산이 필요한지, 학습/추론 단계의 의사 코드를 짧게 보여주세요.\n\n"""${text}"""` },
  related: { kind: 'related', labelKo: '관련 논문', labelEn: 'Related work',
             template: (text) => `다음 문단과 가장 관련 깊은 선행 연구 3편을 한국어로 추천해주세요. 각각 한 줄 요약 + 왜 관련이 있는지 한 문장.\n\n"""${text}"""` },
};

function ReaderScreen({ params }) {
  const { t, lang } = useT();
  const nav = React.useContext(NavContext);
  const auth = useAuth();
  const { papers } = usePapers();

  // Look up the paper for this reader instance — by URL :id, then fall back
  // to the first paper so the demo still works for anonymous users.
  const id = params?.id ?? nav?.params?.id ?? null;
  const paper = (id && papers.find((p) => p.id === id)) ?? papers[0] ?? null;
  const meta = paper?._meta ?? {};
  const pdfUrl = meta.pdf_url || (paper?.arxiv ? `https://arxiv.org/pdf/${paper.arxiv}` : null);
  const arxivId = paper?.arxiv || meta.arxiv_id || null;

  const [aiCards, setAiCards] = React.useState([]);   // [{ id, kind, source, content, pending, saved }]
  const [aiBusy, setAiBusy]   = React.useState(false);
  const [pasteText, setPasteText] = React.useState('');

  async function saveCardToWiki(card) {
    if (!auth.user) { auth.signIn(); return; }
    const body = `**${READER_PROMPTS[card.kind]?.labelKo ?? card.kind} · ${new Date().toLocaleString('ko-KR')}**\n\n> ${card.source.slice(0, 280)}\n\n${card.content}`;
    try {
      const res = await fetch('/api/notes', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ body, anchor: { kind: card.kind, source: 'reader' } }),
      });
      const j = await res.json();
      if (j?.ok) {
        setAiCards((cards) => cards.map((c) => c.id === card.id ? { ...c, saved: true } : c));
      }
    } catch { /* swallow */ }
  }

  async function runReaderPrompt(kind) {
    // Prefer a real text selection (works in the mock page); fall back to the
    // pasted text box so users can analyse passages from the embedded PDF
    // even though the iframe blocks cross-origin selection access.
    const sel =
      ((typeof window !== 'undefined' ? window.getSelection?.()?.toString().trim() : '') || '')
      || pasteText.trim();
    if (!sel) {
      alert(lang === 'ko'
        ? '먼저 텍스트를 선택하거나 우측의 입력란에 분석할 문단을 붙여넣으세요.'
        : 'Select text first or paste a passage into the analyse box on the right.');
      return;
    }
    const p = READER_PROMPTS[kind];
    if (!p) return;
    const id = (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);
    setAiCards((cards) => [{ id, kind, source: sel, content: '', pending: true }, ...cards]);
    setAiBusy(true);
    try {
      const stream = streamChat({
        messages: [{ role: 'user', content: p.template(sel) }],
        model: 'gemini-2.5-flash',
      });
      for await (const chunk of stream) {
        setAiCards((cards) => cards.map((c) => c.id === id ? { ...c, content: c.content + chunk } : c));
      }
    } catch (e) {
      setAiCards((cards) => cards.map((c) => c.id === id ? { ...c, content: c.content + `\n\n_(error: ${e.message})_`, error: true } : c));
    } finally {
      setAiCards((cards) => cards.map((c) => c.id === id ? { ...c, pending: false } : c));
      setAiBusy(false);
    }
  }

  return (
    <div className="pp pp-screen" data-screen-label="04 PDF reader">
      <Sidebar active="library" compact/>

      <div className="pp-main">
        <div className="pp-reader-bar">
          <div className="row gap-3" style={{ minWidth: 0 }}>
            <button className="iconbtn" onClick={() => nav.navigate && nav.navigate('detail', paper ? { id: paper.id } : undefined)}><I name="chevronLeft" size={14}/></button>
            <div className="col" style={{ minWidth: 0 }}>
              <div className="serif ellipsis" style={{ fontSize: 14, color: 'var(--fg-0)' }}>
                {paper?.title || (lang === 'ko' ? '논문을 선택하세요' : 'No paper loaded')}
              </div>
              <div className="row gap-2" style={{ fontSize: 10.5, color: 'var(--fg-3)' }}>
                {paper?.venue && <><span className="mono">{paper.venue}</span><span>·</span></>}
                {paper?.authors?.length > 0 && (
                  <>
                    <span>{paper.authors.slice(0, 2).join(', ')}{paper.authors.length > 2 ? ' et al.' : ''}</span>
                    <span>·</span>
                  </>
                )}
                {arxivId && <span className="mono">arXiv {arxivId}</span>}
                {pdfUrl && (
                  <>
                    <span>·</span>
                    <a href={pdfUrl} target="_blank" rel="noreferrer" className="mono" style={{ color: 'var(--acc-indigo)' }}>
                      {lang === 'ko' ? '새 탭에서 열기' : 'Open in new tab'}
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="row gap-2">
            <div className="pp-reader-progress">
              <div className="pp-reader-progress-bar" style={{ width: '32%' }}/>
              <span className="mono" style={{ fontSize: 10, color: 'var(--fg-2)' }}>32%</span>
            </div>
            <span className="chip warn" style={{ height: 22 }}><I name="zap" size={10}/> {t('rd.diff')}</span>
            <span className="chip ghost" style={{ height: 22 }}>{t('rd.timeleft')}</span>
          </div>

          <div className="row gap-2">
            <button className="btn sm ghost"><I name="layers" size={12}/> {t('rd.outline.btn')}</button>
            <button className="btn sm ghost"><I name="search" size={12}/> {t('rd.find')}</button>
            <button className="btn sm ghost"><I name="figure" size={12}/> {t('rd.figs')}</button>
            <div className="pp-divider-v"/>
            <button className="iconbtn" title="Highlight"><I name="highlight" size={14}/></button>
            <button className="iconbtn" title="Memo"><I name="comment" size={14}/></button>
            <button className="iconbtn" title="Bookmark"><I name="bookmark" size={14}/></button>
            <button className="iconbtn" title="Zoom out"><I name="more" size={14} style={{ transform: 'rotate(90deg)' }}/></button>
            <span className="mono" style={{ fontSize: 11, color: 'var(--fg-2)' }}>112%</span>
          </div>
        </div>

        <div className="pp-content">
          {/* outline rail */}
          <aside className="pp-outline scroll">
            <div className="section-title" style={{ marginBottom: 10 }}>{t('rd.outline')}</div>
            <OutlineRow num="1"   title="Introduction" page="1"/>
            <OutlineRow num="2"   title="Related work" page="3"/>
            <OutlineRow num="3"   title="Method" page="5" current/>
            <OutlineRow num="3.1" title="Deformable 3D Gaussian field" page="5" indent/>
            <OutlineRow num="3.2" title="Diffusion-transformer motion prior" page="7" indent current note="2"/>
            <OutlineRow num="3.3" title="Quantized canonical-id cache" page="9" indent/>
            <OutlineRow num="4"   title="Training" page="11"/>
            <OutlineRow num="5"   title="Experiments" page="14" note="4"/>
            <OutlineRow num="5.1" title="D-NeRF dynamic scenes" page="14" indent/>
            <OutlineRow num="5.2" title="Ablations" page="17" indent/>
            <OutlineRow num="6"   title="Limitations" page="19"/>
            <OutlineRow num="7"   title="Conclusion" page="20"/>

            <div className="section-title" style={{ marginTop: 22, marginBottom: 8 }}>{t('rd.figs')}</div>
            <div className="pp-figs">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className={'pp-fig-thumb' + (i === 4 ? ' is-current' : '')}>
                  <div className="pp-fig-thumb-inner" style={{
                    background: `radial-gradient(circle at ${20 + i * 12}% ${30 + i * 8}%, rgba(136,150,255,0.45), transparent 60%), radial-gradient(circle at ${80 - i * 7}% ${70 - i * 5}%, rgba(180,155,255,0.30), transparent 70%), #14172A`,
                  }}/>
                  <span className="mono" style={{ fontSize: 9, color: 'var(--fg-3)' }}>Fig {i}</span>
                </div>
              ))}
            </div>
          </aside>

          {/* PDF viewport — real PDF via iframe if we have a URL, else
              the original mock page so the screen is still browsable for
              anonymous users or papers without a PDF. */}
          <div className="pp-pdf-stage scroll" style={pdfUrl ? { padding: 0, background: '#1a1a1a' } : undefined}>
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                title={paper?.title || 'PDF'}
                style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                allow="fullscreen"
              />
            ) : (
            <div className="pp-pdf-page">
              <PDFPageContent/>

              {/* selection popover — buttons act on window.getSelection() */}
              <div className="pp-selection-pop" style={{ top: 380, left: 260 }}>
                <div className="pp-selection-arrow"/>
                <button className="pp-selection-btn"><I name="highlight" size={12}/> {t('rd.pop.high')}</button>
                <div className="pp-divider-v"/>
                <button className="pp-selection-btn" onClick={() => runReaderPrompt('simple')} disabled={aiBusy}>
                  <I name="sparkles" size={12} style={{ color: 'var(--acc-indigo)' }}/> {t('rd.pop.simple')}
                </button>
                <button className="pp-selection-btn" onClick={() => runReaderPrompt('formula')} disabled={aiBusy}>
                  <I name="formula" size={12}/> {t('rd.pop.formula')}
                </button>
                <button className="pp-selection-btn" onClick={() => runReaderPrompt('impl')} disabled={aiBusy}>
                  <I name="code" size={12}/> {t('rd.pop.impl')}
                </button>
                <button className="pp-selection-btn" onClick={() => runReaderPrompt('related')} disabled={aiBusy}>
                  <I name="link" size={12}/> {t('rd.pop.related')}
                </button>
                <button className="pp-selection-btn"><I name="book" size={12}/> {t('rd.pop.wiki')}</button>
                <button className="pp-selection-btn"><I name="kanban" size={12}/> {t('rd.pop.proj')}</button>
              </div>
            </div>
            )}
          </div>

          {/* AI / notes rail */}
          <RailRight id="reader-notes" defaultWidth={360} min={300} max={500} className="pp-notes-rail scroll">
            <div className="pp-notes-tabs">
              <button className="pp-notes-tab is-active"><I name="sparkles" size={12}/> {t('rd.tab.ai')}</button>
              <button className="pp-notes-tab"><I name="comment" size={12}/> {t('rd.tab.notes')} <span className="mono" style={{ color: 'var(--fg-3)' }}>8</span></button>
              <button className="pp-notes-tab"><I name="highlight" size={12}/> {t('rd.tab.high')} <span className="mono" style={{ color: 'var(--fg-3)' }}>21</span></button>
              <button className="pp-notes-tab"><I name="figure" size={12}/> {t('rd.tab.figs')}</button>
              <button className="pp-notes-tab"><I name="table" size={12}/> {t('rd.tab.tabs')}</button>
            </div>

            <div className="col gap-3">
              {/* Paste box — PDF iframes are cross-origin so window.getSelection
                  can't see text inside them. Users select in the PDF, copy
                  with Ctrl+C, paste here, then hit one of the AI buttons. */}
              {pdfUrl && (
                <div className="pp-ai-card">
                  <div className="row gap-2" style={{ alignItems: 'baseline' }}>
                    <I name="sparkles" size={12} style={{ color: 'var(--acc-indigo)' }}/>
                    <span style={{ fontSize: 11, color: 'var(--acc-indigo)', textTransform: 'uppercase', letterSpacing: '0.08em' }} className="mono">
                      {lang === 'ko' ? '문단 분석' : 'Analyse passage'}
                    </span>
                    {pasteText && (
                      <button className="iconbtn" style={{ marginLeft: 'auto' }} onClick={() => setPasteText('')}>
                        <I name="close" size={12}/>
                      </button>
                    )}
                  </div>
                  <textarea
                    rows={4}
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder={lang === 'ko' ? 'PDF에서 텍스트를 복사해 여기에 붙여넣고 아래 버튼을 누르세요.' : 'Copy text from the PDF and paste it here, then click an action below.'}
                    style={{
                      width: '100%', marginTop: 8, padding: 8, fontSize: 12,
                      background: 'var(--bg-2)', border: '1px solid var(--border-1)',
                      borderRadius: 6, color: 'var(--fg-0)', resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                  <div className="row gap-2" style={{ marginTop: 8, flexWrap: 'wrap' }}>
                    <button className="btn sm" onClick={() => runReaderPrompt('simple')} disabled={aiBusy || !pasteText.trim()}>
                      <I name="sparkles" size={11}/> {lang === 'ko' ? '간단 요약' : 'Summary'}
                    </button>
                    <button className="btn sm" onClick={() => runReaderPrompt('formula')} disabled={aiBusy || !pasteText.trim()}>
                      <I name="formula" size={11}/> {lang === 'ko' ? '수식 설명' : 'Math'}
                    </button>
                    <button className="btn sm" onClick={() => runReaderPrompt('impl')} disabled={aiBusy || !pasteText.trim()}>
                      <I name="code" size={11}/> {lang === 'ko' ? '구현 분석' : 'Impl'}
                    </button>
                    <button className="btn sm" onClick={() => runReaderPrompt('related')} disabled={aiBusy || !pasteText.trim()}>
                      <I name="link" size={11}/> {lang === 'ko' ? '관련 논문' : 'Related'}
                    </button>
                  </div>
                </div>
              )}

              {/* live AI cards (selection → Gemini) */}
              {aiCards.map((card) => {
                const label = READER_PROMPTS[card.kind] && (lang === 'ko' ? READER_PROMPTS[card.kind].labelKo : READER_PROMPTS[card.kind].labelEn);
                return (
                  <div key={card.id} className="pp-ai-card">
                    <div className="row gap-2" style={{ alignItems: 'baseline' }}>
                      <I name="sparkles" size={12} style={{ color: 'var(--acc-violet)' }}/>
                      <span style={{ fontSize: 11, color: 'var(--acc-violet)', textTransform: 'uppercase', letterSpacing: '0.08em' }} className="mono">{label || card.kind}</span>
                      {card.pending && <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', marginLeft: 'auto' }}>streaming…</span>}
                    </div>
                    <div className="pp-ai-quoted" style={{ fontStyle: 'italic' }}>
                      {card.source.length > 200 ? card.source.slice(0, 200) + '…' : card.source}
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--fg-1)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                      {card.content}
                      {card.pending && <span className="pp-asst-cursor" aria-hidden>▍</span>}
                    </div>
                    {!card.pending && card.content && (
                      <div className="row gap-2" style={{ marginTop: 10 }}>
                        <button
                          className="btn sm"
                          onClick={() => saveCardToWiki(card)}
                          disabled={card.saved}
                        >
                          <I name="book" size={11}/>{' '}
                          {card.saved
                            ? (lang === 'ko' ? '위키에 저장됨' : 'Saved to wiki')
                            : (lang === 'ko' ? '위키에 저장' : 'Save to wiki')}
                        </button>
                        <button
                          className="btn sm ghost"
                          onClick={() => navigator.clipboard?.writeText(card.content).catch(() => {})}
                        >
                          <I name="copy" size={11}/> {lang === 'ko' ? '복사' : 'Copy'}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* AI explain card */}
              <div className="pp-ai-card">
                <div className="row gap-2" style={{ alignItems: 'baseline' }}>
                  <I name="sparkles" size={12} style={{ color: 'var(--acc-violet)' }}/>
                  <span style={{ fontSize: 11, color: 'var(--acc-violet)', textTransform: 'uppercase', letterSpacing: '0.08em' }} className="mono">{t('rd.ai.kicker')}</span>
                  <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)', marginLeft: 'auto' }}>p.7 · §3.2</span>
                </div>
                <div className="pp-ai-quoted">
                  "…the canonical-id cache replaces per-frame MLP evaluation with a lookup into a quantized codebook of motion offsets, indexed by Gaussian id and frame."
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--fg-1)', lineHeight: 1.55 }}>
                  <span style={{ color: 'var(--fg-0)' }}>In code:</span> at training time you keep a vanilla MLP <code className="mono pp-code">g_θ(id, t)</code>; at the end of training you sweep all <code className="mono pp-code">(id, t)</code> pairs, quantize the outputs to a 4-bit codebook (around <span className="mono">64 entries</span>), and store a per-Gaussian index map. Inference becomes a single texture fetch — no MLP forward — which is why FPS jumps 3.1×.
                </div>
                <div className="row gap-2" style={{ marginTop: 10 }}>
                  <button className="btn sm"><I name="copy" size={11}/> {t('rd.copy')}</button>
                  <button className="btn sm ghost"><I name="book" size={11}/> {t('rd.save.wiki')}</button>
                  <button className="btn sm ghost"><I name="kanban" size={11}/> {t('rd.pin.proj')}</button>
                </div>
              </div>

              {/* formula card */}
              <div className="pp-ai-card">
                <div className="row gap-2" style={{ alignItems: 'baseline' }}>
                  <I name="formula" size={12} style={{ color: 'var(--acc-cyan)' }}/>
                  <span style={{ fontSize: 11, color: 'var(--acc-cyan)', textTransform: 'uppercase', letterSpacing: '0.08em' }} className="mono">{t('rd.formula.kicker')}</span>
                </div>
                <div className="pp-formula">
                  μ<sub>i,t</sub> = μ<sub>i</sub><sup>can</sup> + <span style={{ color: 'var(--acc-violet)' }}>𝒟</span>(id<sub>i</sub>, t)
                </div>
                <div style={{ fontSize: 12, color: 'var(--fg-1)' }}>
                  {t('rd.formula.body')}
                </div>
              </div>

              {/* related */}
              <div className="pp-ai-card pp-related-card">
                <div className="section-title" style={{ marginBottom: 6 }}>{t('rd.related.title')}</div>
                <RelatedRow title="Deformable 3D Gaussians (CVPR 24)" reason={t('rd.related.r1')}/>
                <RelatedRow title="K-Planes for dynamic NVS (CVPR 23)" reason={t('rd.related.r2')}/>
                <RelatedRow title="4DGS-Cache (SIGGRAPH 26)" reason={t('rd.related.r3')}/>
              </div>

              {/* user memo */}
              <div className="pp-memo-card">
                <div className="row gap-2" style={{ alignItems: 'baseline' }}>
                  <I name="comment" size={11} style={{ color: 'var(--warn)' }}/>
                  <span style={{ fontSize: 11, color: 'var(--warn)', textTransform: 'uppercase', letterSpacing: '0.08em' }} className="mono">{t('rd.memo.kicker')}</span>
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--fg-0)', marginTop: 6, lineHeight: 1.55 }}>
                  {t('rd.memo.body')}
                </div>
                <div className="row gap-2" style={{ marginTop: 6, fontSize: 10, color: 'var(--fg-3)' }}>
                  <span className="tag">rack-twin</span>
                  <span className="tag">to-do</span>
                </div>
              </div>
            </div>

            <div className="pp-ask-passage">
              <I name="sparkles" size={13} style={{ color: 'var(--acc-indigo)' }}/>
              <span style={{ flex: 1, color: 'var(--fg-3)', fontSize: 12 }}>{t('rd.ask.passage')}</span>
              <span className="pp-kbd">⌘K</span>
            </div>
          </RailRight>
        </div>
      </div>
    </div>
  );
}

function OutlineRow({ num, title, page, current, indent, note }) {
  const [hover, setHover] = React.useState(false);
  return (
    <div className={'pp-outline-row ' + (current ? 'is-current' : '') + (indent ? ' is-indent' : '')}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-3)', width: 26 }}>{num}</span>
      <span className="grow ellipsis" style={{ fontSize: 12 }}>{title}</span>
      {note && <span className="pp-outline-note">{note}</span>}
      <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>p.{page}</span>
    </div>
  );
}

function PDFPageContent() {
  return (
    <div className="pp-pdf-content">
      <div className="pp-pdf-head">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 9, color: '#5a5a5a', letterSpacing: '0.18em', textTransform: 'uppercase' }}>GS-DiT · §3.2 Diffusion-transformer motion prior</span>
          <span className="mono" style={{ fontSize: 9, color: '#5a5a5a' }}>7</span>
        </div>
      </div>

      <h2 style={{ fontSize: 13, fontWeight: 700, color: '#1c1c1c', margin: '14px 0 8px' }}>3.2 Diffusion-transformer motion prior</h2>

      <p className="pp-pdf-p">
        While a pure MLP deformation field <span className="pp-pdf-cite">[12, 24]</span> is sufficient to fit individual dynamic scenes, we observe that it generalizes poorly across captures of similar content — every new sequence demands a fresh optimization. We instead distill a learned motion prior from a corpus of <span className="pp-pdf-num">28k</span> short video clips, parameterized as a small diffusion transformer (DiT) <span className="pp-pdf-cite">[31]</span> with <span className="pp-pdf-num">112M</span> parameters.
      </p>

      <p className="pp-pdf-p">
        <span className="pp-highlight pp-h-yellow">Concretely, we treat the per-Gaussian displacement Δμ<sub>i,t</sub> as the variable to be denoised, conditioned on the canonical Gaussian feature <span className="mono">f<sub>i</sub></span>, the relative time embedding <span className="mono">τ(t)</span>, and a coarse scene-token sequence produced by a frozen visual backbone.</span> Unlike prior work that trains the diffusion model jointly with the Gaussian field <span className="pp-pdf-cite">[44]</span>, we keep the two stages strictly decoupled — the DiT is trained once and frozen, and downstream
      </p>

      <p className="pp-pdf-p">
        scenes only optimize the canonical positions, scales and color-features. This separation is what makes our system <span className="pp-pdf-emph">single-GPU trainable in 11 hours</span> on a 4090.
      </p>

      <h3 style={{ fontSize: 11.5, fontWeight: 700, color: '#1c1c1c', margin: '16px 0 6px' }}>3.2.1 Canonical-id cache</h3>

      <p className="pp-pdf-p">
        At inference, naive evaluation of the DiT per-frame eliminates the rendering-speed advantage of 3D Gaussian Splatting <span className="pp-pdf-cite">[18]</span>. <span className="pp-highlight pp-h-violet">We sidestep this with a quantized canonical-id cache: after training, we sweep all <span className="mono">(id, t)</span> pairs and store the predicted displacement in a 4-bit codebook with <span className="mono">K = 64</span> entries, indexed by a per-Gaussian map. At render time, the deformation step collapses to a single texture fetch.</span> This is empirically lossless for our scenes (Tab. 4) and brings inference cost back to vanilla 3DGS.
      </p>

      <div className="pp-pdf-eq">
        μ<sub>i,t</sub> = μ<sub>i</sub><sup>can</sup> + <span style={{ color: '#5B68E0' }}>𝒟<sub>θ</sub></span>(id<sub>i</sub>, t)
        &nbsp;&nbsp;&nbsp;<span style={{ color: '#5a5a5a' }}>(7)</span>
      </div>

      <p className="pp-pdf-p">
        where <span className="mono">𝒟<sub>θ</sub></span> is the cached lookup at inference and the DiT denoiser at training. The codebook is shared across Gaussians but indexed independently — a choice we validate in §5.2.
      </p>
    </div>
  );
}

function RelatedRow({ title, reason }) {
  return (
    <div className="row gap-2" style={{ padding: '6px 0', alignItems: 'flex-start' }}>
      <div style={{ width: 4, height: 4, borderRadius: 999, background: 'var(--acc-indigo)', marginTop: 7, flexShrink: 0 }}/>
      <div className="col grow">
        <div className="ellipsis" style={{ fontSize: 11.5, color: 'var(--fg-0)' }}>{title}</div>
        <div style={{ fontSize: 10.5, color: 'var(--fg-2)' }}>{reason}</div>
      </div>
    </div>
  );
}

if (!document.getElementById('pp-reader-styles')) {
  const s = document.createElement('style');
  s.id = 'pp-reader-styles';
  s.textContent = `
  .pp-reader-bar {
    height: 56px;
    border-bottom: 1px solid var(--border-1);
    background: var(--bg-1);
    padding: 0 16px;
    display: flex; align-items: center; gap: 16px;
    justify-content: space-between;
  }
  .pp-reader-progress {
    display: flex; align-items: center; gap: 8px;
    width: 160px;
    height: 22px; padding: 0 8px;
    border-radius: 6px;
    background: var(--bg-3);
    border: 1px solid var(--border-1);
  }
  .pp-reader-progress::before {
    content: ''; height: 3px; flex: 1; border-radius: 999px;
    background: linear-gradient(90deg, var(--acc-indigo) 32%, rgba(255,255,255,0.06) 32%);
  }
  .pp-reader-progress-bar { display: none; }

  .pp-outline {
    width: 220px; flex-shrink: 0;
    border-right: 1px solid var(--border-1);
    background: var(--bg-1);
    padding: 18px 12px;
    overflow: auto;
  }
  .pp-outline-row {
    display: flex; align-items: center; gap: 8px;
    padding: 5px 8px; border-radius: 5px;
    color: var(--fg-1);
    cursor: pointer;
  }
  .pp-outline-row:hover { background: rgba(255,255,255,0.025); }
  .pp-outline-row.is-current { background: rgba(136,150,255,0.10); color: var(--fg-0); box-shadow: inset 2px 0 0 var(--acc-indigo); }
  .pp-outline-row.is-indent { padding-left: 22px; }
  .pp-outline-note {
    font-family: var(--font-mono); font-size: 9px;
    color: var(--warn); background: var(--warn-bg);
    padding: 1px 5px; border-radius: 999px;
  }
  .pp-figs { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
  .pp-fig-thumb { display: flex; flex-direction: column; gap: 4px; align-items: center; padding: 4px; border-radius: 6px; cursor: pointer; }
  .pp-fig-thumb.is-current { background: rgba(136,150,255,0.10); box-shadow: 0 0 0 1px rgba(136,150,255,0.30); }
  .pp-fig-thumb-inner {
    width: 100%; aspect-ratio: 4/3; border-radius: 4px;
    border: 1px solid var(--border-2);
    box-shadow: 0 2px 8px rgba(0,0,0,0.35);
  }

  .pp-pdf-stage {
    flex: 1; min-width: 0;
    overflow: auto;
    padding: 24px;
    background: radial-gradient(ellipse at 50% 0%, rgba(136,150,255,0.04), transparent 60%), var(--bg-0);
    display: flex; justify-content: center;
  }
  .pp-pdf-page {
    width: 612px; min-height: 792px;
    background: #f2efe8;
    color: #1c1c1c;
    padding: 56px 64px;
    box-shadow: 0 24px 60px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04);
    border-radius: 4px;
    position: relative;
    font-family: 'Newsreader', 'Instrument Serif', Georgia, serif;
  }
  .pp-pdf-content { font-size: 10.5px; line-height: 1.55; color: #1c1c1c; }
  .pp-pdf-head { padding-bottom: 8px; border-bottom: 0.5px solid #aaa; }
  .pp-pdf-p { margin: 0 0 10px; text-align: justify; hyphens: auto; }
  .pp-pdf-cite { color: #5B68E0; }
  .pp-pdf-num { font-family: 'Times New Roman', serif; }
  .pp-pdf-emph { font-style: italic; }
  .pp-pdf-eq {
    font-family: 'Cambria Math', 'Times New Roman', serif;
    font-style: italic;
    text-align: center;
    padding: 12px 0;
    font-size: 13px;
  }
  .pp-highlight {
    background: rgba(232,184,106,0.45);
    padding: 0 2px; border-radius: 2px;
    box-shadow: 0 0 0 0.5px rgba(232,184,106,0.6);
  }
  .pp-h-violet { background: rgba(180,155,255,0.50); box-shadow: 0 0 0 0.5px rgba(180,155,255,0.7); }

  .pp-selection-pop {
    position: absolute;
    display: flex; align-items: center; gap: 1px;
    padding: 4px;
    background: #0F1219;
    border: 1px solid rgba(136,150,255,0.30);
    border-radius: 10px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.6), 0 0 24px rgba(136,150,255,0.20);
    z-index: 5;
  }
  .pp-selection-arrow {
    position: absolute; top: -5px; left: 30px;
    width: 8px; height: 8px;
    background: #0F1219;
    border-top: 1px solid rgba(136,150,255,0.30);
    border-left: 1px solid rgba(136,150,255,0.30);
    transform: rotate(45deg);
  }
  .pp-selection-btn {
    height: 26px; padding: 0 8px;
    display: flex; align-items: center; gap: 5px;
    border-radius: 5px;
    color: var(--fg-1); font-size: 11px;
    background: transparent; border: 0; cursor: pointer;
    white-space: nowrap;
  }
  .pp-selection-btn:hover { background: rgba(255,255,255,0.06); color: var(--fg-0); }

  .pp-notes-rail {
    width: 360px; flex-shrink: 0;
    border-left: 1px solid var(--border-1);
    background: var(--bg-1);
    padding: 18px;
    overflow: auto;
    display: flex; flex-direction: column; gap: 14px;
    position: relative;
  }
  .pp-notes-tabs { display: flex; gap: 2px; padding: 3px; border: 1px solid var(--border-1); background: var(--bg-2); border-radius: 8px; }
  .pp-notes-tab {
    flex: 1; padding: 5px 6px;
    display: flex; align-items: center; justify-content: center; gap: 4px;
    font-size: 11px; color: var(--fg-2);
    border-radius: 5px; cursor: pointer;
  }
  .pp-notes-tab.is-active { background: var(--bg-4); color: var(--fg-0); box-shadow: 0 1px 0 rgba(255,255,255,0.05) inset; }

  .pp-ai-card {
    background: var(--bg-2);
    border: 1px solid var(--border-1);
    border-radius: 10px;
    padding: 12px;
    display: flex; flex-direction: column; gap: 8px;
  }
  .pp-ai-quoted {
    padding: 8px 10px;
    background: rgba(180,155,255,0.06);
    border-left: 2px solid var(--acc-violet);
    border-radius: 0 4px 4px 0;
    font-size: 11.5px;
    color: var(--fg-1);
    font-style: italic;
    line-height: 1.5;
  }
  .pp-code {
    font-size: 10.5px;
    background: rgba(255,255,255,0.05);
    padding: 1px 4px;
    border-radius: 3px;
    color: var(--acc-cyan);
  }
  .pp-formula {
    padding: 10px 12px;
    background: var(--bg-3);
    border-radius: 6px;
    font-family: 'Cambria Math', serif;
    font-style: italic;
    font-size: 14px;
    color: var(--fg-0);
    text-align: center;
  }
  .pp-memo-card {
    background: linear-gradient(180deg, rgba(232,184,106,0.06), transparent);
    border: 1px solid rgba(232,184,106,0.25);
    border-radius: 10px;
    padding: 12px;
  }

  .pp-ask-passage {
    margin-top: auto;
    display: flex; align-items: center; gap: 8px;
    padding: 9px 11px;
    border: 1px solid rgba(136,150,255,0.25);
    background: rgba(136,150,255,0.04);
    border-radius: 8px;
    position: sticky; bottom: 0;
  }

  .pp-related-card { padding: 12px; }
  `;
  document.head.appendChild(s);
}

export { ReaderScreen };
